import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Button, Card, Divider, Drawer, Form, Input, InputNumber, message, Modal, Select,
  Space, Switch, Table, Tag, Typography, Upload, Spin, Tooltip,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { UploadFile } from 'antd/es/upload/interface';
import {
  PlusOutlined, ReloadOutlined, ExperimentOutlined, UploadOutlined, ReloadOutlined as RefreshIcon,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import request from '@/utils/request';
import { CosImage } from '@/components/CosImage';
import { uploadFileToCos } from '@/utils/upload';
import type {
  CreateImageModelInput,
  ImageInputField,
  ImageModelDTO,
  ImagePricingEntry,
  UpdateImageModelInput,
} from '@zero/shared';

const { Title, Text, Paragraph } = Typography;

interface ListResponse {
  items: ImageModelDTO[];
  falConfigured: boolean;
}

interface TestResult {
  requestId: string;
  images: Array<{ id: number; cosKey: string; accessUrl: string }>;
  cost: { costUsd: string | null; priceMatchKey: string | null; costSource: string | null };
  latencyMs: number;
  cacheHit: boolean | null;
  uploadLatencyMs: number | null;
  timings: {
    totalMs: number;
    uploadMs: number | null;
    inferenceMs: number | null;
    downloadMs: number | null;
  };
}

interface TestHistoryItem {
  id: number;
  createdAt: string;
  status: 'success' | 'failed' | 'timeout';
  requestId: string | null;
  inputPayload: Record<string, unknown> | null;
  inputSummary: { promptPreview?: string | null; effectiveParams?: Record<string, unknown> } | null;
  outputImageIds: number[];
  outputImageCosKeys: (string | null)[];
  costUsd: string | null;
  priceMatchKey: string | null;
  costSource: string | null;
  latencyMs: number | null;
  uploadLatencyMs: number | null;
  inferenceMs: number | null;
  downloadMs: number | null;
  cacheHit: boolean | null;
  errorCode: string | null;
  errorMessage: string | null;
  errorUpstream: unknown | null;
}

const ImageModelsPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<ImageModelDTO[]>([]);
  const [falConfigured, setFalConfigured] = useState(false);
  const [editing, setEditing] = useState<ImageModelDTO | 'new' | null>(null);
  const [testing, setTesting] = useState<ImageModelDTO | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await request.get<ListResponse>('/admin/image-models');
      setItems(res.data.items);
      setFalConfigured(res.data.falConfigured);
    } catch (err) {
      message.error((err as Error).message || '加载失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleDelete = async (record: ImageModelDTO) => {
    Modal.confirm({
      title: `删除模型 "${record.slug}"?`,
      content: '此操作不可撤销，关联的调用日志的 imageModelId 会被置空。',
      okType: 'danger',
      onOk: async () => {
        try {
          await request.delete(`/admin/image-models/${record.id}`);
          message.success('已删除');
          load();
        } catch (err) {
          message.error((err as Error).message || '删除失败');
        }
      },
    });
  };

  const handleToggleActive = async (record: ImageModelDTO, active: boolean) => {
    try {
      await request.patch(`/admin/image-models/${record.id}`, { active });
      load();
    } catch (err) {
      message.error((err as Error).message || '更新失败');
    }
  };

  const openTest = (model: ImageModelDTO) => {
    setTesting(model);
  };

  const columns: ColumnsType<ImageModelDTO> = [
    { title: 'Slug', dataIndex: 'slug', render: (v) => <Text code>{v}</Text> },
    {
      title: 'Endpoint',
      dataIndex: 'endpoint',
      render: (v) => <Text style={{ fontSize: 12 }}>{v}</Text>,
    },
    { title: '名称', dataIndex: 'displayName' },
    {
      title: '定价',
      dataIndex: 'pricing',
      render: (p: ImageModelDTO['pricing']) => {
        const n = p.entries?.length ?? 0;
        if (n === 1 && Object.keys(p.entries[0].match).length === 0) {
          return <Tag color="blue">固定 ${p.entries[0].unitPriceUsd.toFixed(4)}</Tag>;
        }
        return <Tag color="purple">{n} 条条目</Tag>;
      },
    },
    {
      title: '状态',
      dataIndex: 'active',
      render: (_, r) => (
        <Switch
          checked={r.active}
          onChange={(v) => handleToggleActive(r, v)}
          size="small"
        />
      ),
    },
    {
      title: '操作',
      render: (_, r) => (
        <Space>
          <Button size="small" onClick={() => setEditing(r)}>
            编辑
          </Button>
          <Button
            size="small"
            icon={<ExperimentOutlined />}
            onClick={() => openTest(r)}
            disabled={!falConfigured}
          >
            测试
          </Button>
          <Button size="small" danger onClick={() => handleDelete(r)}>
            删除
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Title level={3} style={{ marginTop: 0 }}>图像生成模型</Title>
      {!falConfigured && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="fal.ai 未配置"
          description="请在项目根目录 .env 中设置 FAL_KEY 后重启服务。"
        />
      )}
      <Card
        extra={
          <Space>
            <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>
              刷新
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setEditing('new')}>
              新建模型
            </Button>
          </Space>
        }
      >
        <Table
          rowKey="id"
          columns={columns}
          dataSource={items}
          pagination={false}
          loading={loading}
        />
      </Card>

      {editing && (
        <ModelDrawer
          open={!!editing}
          model={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      )}

      {testing && (
        <TestDrawer
          model={testing}
          onClose={() => setTesting(null)}
        />
      )}
    </div>
  );
};

// =====================================================================
// TestDrawer — actual interactive test workspace for a model
// =====================================================================

interface TestDrawerProps {
  model: ImageModelDTO;
  onClose: () => void;
}

const TestDrawer: React.FC<TestDrawerProps> = ({ model, onClose }) => {
  const [sourceImage, setSourceImage] = useState<{ id: number; accessUrl: string; cosKey: string } | null>(null);
  const [uploading, setUploading] = useState(false);
  const [inputs, setInputs] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<TestResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [history, setHistory] = useState<TestHistoryItem[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Initialize defaults from sampleInput + schema defaults
  useEffect(() => {
    const init: Record<string, string> = {};
    const sample = (model.metadata?.sampleInput as Record<string, unknown> | undefined) ?? {};
    for (const f of model.inputSchema.fields) {
      if (f.type === 'image_url' || f.type === 'image_urls') continue;
      const sampled = sample[f.key];
      if (sampled != null) init[f.key] = String(sampled);
      else if (f.defaultValue != null) init[f.key] = String(f.defaultValue);
    }
    setInputs(init);
  }, [model]);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const res = await request.get<{ items: TestHistoryItem[] }>(
        `/admin/image-models/${model.id}/test-history?limit=20`,
      );
      setHistory(res.data.items);
    } catch (err) {
      console.error(err);
    } finally {
      setHistoryLoading(false);
    }
  }, [model.id]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const requiresSourceImage = model.inputSchema.fields.some(
    (f) => (f.type === 'image_url' || f.type === 'image_urls') && f.required,
  );

  const handleUpload = async (file: File) => {
    setUploading(true);
    setError(null);
    try {
      const uploaded = await uploadFileToCos(file);
      setSourceImage({ id: uploaded.id, accessUrl: uploaded.accessUrl, cosKey: uploaded.cosKey });
      message.success('源图已上传');
    } catch (err) {
      message.error((err as Error).message || '上传失败');
    } finally {
      setUploading(false);
    }
    return false; // prevent antd default upload
  };

  const handleSubmit = async () => {
    if (requiresSourceImage && !sourceImage) {
      message.warning('该模型需要源图,请先上传');
      return;
    }
    setSubmitting(true);
    setError(null);
    setResult(null);
    try {
      const inputsPayload: Record<string, unknown> = {};
      for (const f of model.inputSchema.fields) {
        if (f.type === 'image_url' || f.type === 'image_urls') continue;
        const v = inputs[f.key];
        if (v == null || v === '') continue;
        inputsPayload[f.key] = f.type === 'number' ? Number(v) : v;
      }
      const res = await request.post<TestResult>(`/admin/image-models/${model.id}/test`, {
        inputs: inputsPayload,
        sourceImageId: sourceImage?.id,
      });
      setResult(res.data);
      loadHistory();
    } catch (err) {
      setError((err as Error).message || '测试失败');
    } finally {
      setSubmitting(false);
    }
  };

  const reuseHistoryParams = (h: TestHistoryItem) => {
    if (!h.inputPayload) {
      message.info('该历史记录无 input 信息');
      return;
    }
    const next: Record<string, string> = {};
    for (const f of model.inputSchema.fields) {
      if (f.type === 'image_url' || f.type === 'image_urls') continue;
      const v = (h.inputPayload as Record<string, unknown>)[f.key];
      if (v != null) next[f.key] = String(v);
    }
    setInputs(next);
    message.success('已填入该次参数,源图需要重新上传');
  };

  return (
    <Drawer
      title={`测试: ${model.slug}`}
      open
      onClose={onClose}
      width={920}
      extra={
        <Space>
          <Button onClick={onClose}>关闭</Button>
        </Space>
      }
    >
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 16 }}>
        {/* Left: form + result */}
        <div>
          <Card size="small" title="参数">
            {requiresSourceImage && (
              <Form.Item label="源图(必选)">
                {sourceImage ? (
                  <Space direction="vertical">
                    <img
                      src={sourceImage.accessUrl}
                      alt="source"
                      style={{ maxWidth: 320, maxHeight: 240, borderRadius: 4 }}
                    />
                    <Space>
                      <Upload
                        accept="image/*"
                        showUploadList={false}
                        beforeUpload={(file) => { handleUpload(file as File); return false; }}
                      >
                        <Button icon={<UploadOutlined />} loading={uploading}>换一张</Button>
                      </Upload>
                      <Button onClick={() => setSourceImage(null)}>清空</Button>
                    </Space>
                  </Space>
                ) : (
                  <Upload
                    accept="image/*"
                    showUploadList={false}
                    beforeUpload={(file) => { handleUpload(file as File); return false; }}
                  >
                    <Button icon={<UploadOutlined />} loading={uploading}>上传源图</Button>
                  </Upload>
                )}
              </Form.Item>
            )}

            {model.inputSchema.fields
              .filter((f) => f.type !== 'image_url' && f.type !== 'image_urls')
              .map((f) => (
                <DynamicAdminField
                  key={f.key}
                  field={f}
                  value={inputs[f.key] ?? ''}
                  onChange={(v) => setInputs((prev) => ({ ...prev, [f.key]: v }))}
                />
              ))}

            <Button
              type="primary"
              loading={submitting}
              onClick={handleSubmit}
              style={{ marginTop: 8 }}
            >
              {submitting ? '生成中…' : '运行测试'}
            </Button>
          </Card>

          {error && (
            <Alert
              type="error"
              message="失败"
              description={error}
              style={{ marginTop: 16 }}
              showIcon
              closable
              onClose={() => setError(null)}
            />
          )}

          {result && (
            <Card size="small" title="生成结果" style={{ marginTop: 16 }}>
              <TimingBreakdown
                timings={result.timings}
                cacheHit={result.cacheHit}
              />
              <Space wrap style={{ marginTop: 8, marginBottom: 12 }}>
                <Tag color="gold">
                  ${result.cost.costUsd ? Number(result.cost.costUsd).toFixed(6) : '—'}
                </Tag>
                {result.cost.priceMatchKey && <Tag>{result.cost.priceMatchKey}</Tag>}
                {result.cost.costSource && <Tag color="purple">{result.cost.costSource}</Tag>}
              </Space>
              <Space wrap>
                {result.images.map((img) => (
                  <CosImage key={img.id} cosKey={img.cosKey} maxWidth={280} maxHeight={280} />
                ))}
              </Space>
            </Card>
          )}
        </div>

        {/* Right: history */}
        <div>
          <Card
            size="small"
            title="测试历史"
            extra={
              <Tooltip title="刷新历史">
                <Button
                  size="small"
                  icon={<RefreshIcon />}
                  onClick={loadHistory}
                  loading={historyLoading}
                />
              </Tooltip>
            }
          >
            {history.length === 0 ? (
              <Text type="secondary">还没有测试记录</Text>
            ) : (
              <Space direction="vertical" style={{ width: '100%' }} size="small">
                {history.map((h) => (
                  <HistoryRow key={h.id} item={h} onReuse={() => reuseHistoryParams(h)} />
                ))}
              </Space>
            )}
          </Card>
        </div>
      </div>
    </Drawer>
  );
};

// Visual breakdown of step timings as a stacked bar + tag legend.
// Each step segment is colored and sized proportionally to its duration.
interface TimingBreakdownProps {
  timings: {
    totalMs: number;
    uploadMs: number | null;
    inferenceMs: number | null;
    downloadMs: number | null;
  };
  cacheHit: boolean | null;
}

const TimingBreakdown: React.FC<TimingBreakdownProps> = ({ timings, cacheHit }) => {
  const { totalMs, uploadMs, inferenceMs, downloadMs } = timings;
  const accounted = (uploadMs ?? 0) + (inferenceMs ?? 0) + (downloadMs ?? 0);
  const otherMs = Math.max(totalMs - accounted, 0);

  const segments = [
    {
      label: cacheHit ? '上传(命中)' : '上传',
      ms: uploadMs ?? 0,
      color: cacheHit ? '#52c41a' : '#fa8c16',
    },
    { label: '推理', ms: inferenceMs ?? 0, color: '#1677ff' },
    { label: '回写', ms: downloadMs ?? 0, color: '#722ed1' },
    { label: '其他', ms: otherMs, color: '#bfbfbf' },
  ].filter((s) => s.ms > 0);

  const total = totalMs || 1;
  return (
    <div>
      <div
        style={{
          display: 'flex',
          height: 8,
          borderRadius: 4,
          overflow: 'hidden',
          background: '#f0f0f0',
          marginBottom: 6,
        }}
      >
        {segments.map((s) => (
          <Tooltip key={s.label} title={`${s.label} ${s.ms} ms`}>
            <div style={{ width: `${(s.ms / total) * 100}%`, background: s.color }} />
          </Tooltip>
        ))}
      </div>
      <Space size={4} wrap>
        <Tag color="default">总 {totalMs} ms</Tag>
        {uploadMs != null && (
          <Tag color={cacheHit ? 'green' : 'orange'}>
            上传 {uploadMs} ms{cacheHit ? ' (命中)' : ''}
          </Tag>
        )}
        {inferenceMs != null && <Tag color="blue">推理 {inferenceMs} ms</Tag>}
        {downloadMs != null && <Tag color="purple">回写 {downloadMs} ms</Tag>}
      </Space>
    </div>
  );
};

const HistoryRow: React.FC<{ item: TestHistoryItem; onReuse: () => void }> = ({ item, onReuse }) => {
  const [expanded, setExpanded] = useState(false);
  const promptPreview =
    item.inputSummary?.promptPreview ||
    (typeof (item.inputPayload as Record<string, unknown>)?.prompt === 'string'
      ? String((item.inputPayload as Record<string, unknown>).prompt).slice(0, 60)
      : '');
  const thumbnailKey = item.outputImageCosKeys.find((k): k is string => !!k);
  const hasUpstream = item.errorUpstream != null;

  return (
    <Card
      size="small"
      style={{
        background: item.status === 'success' ? '#fafafa' : '#fff5f5',
        borderLeft:
          item.status === 'success'
            ? '3px solid #52c41a'
            : item.status === 'timeout'
            ? '3px solid #fa8c16'
            : '3px solid #ff4d4f',
      }}
    >
      <Space direction="vertical" size={4} style={{ width: '100%' }}>
        <Space size={6}>
          <Text style={{ fontSize: 11, color: '#999' }}>
            {dayjs(item.createdAt).format('MM-DD HH:mm:ss')}
          </Text>
          <Tag color="gold" style={{ marginInlineEnd: 0 }}>
            {item.costUsd ? `$${Number(item.costUsd).toFixed(6)}` : '—'}
          </Tag>
          {item.cacheHit != null && (
            <Tag color={item.cacheHit ? 'green' : 'orange'} style={{ marginInlineEnd: 0 }}>
              {item.cacheHit ? 'HIT' : 'MISS'}
            </Tag>
          )}
        </Space>
        {item.latencyMs != null && (
          <TimingBreakdown
            timings={{
              totalMs: item.latencyMs,
              uploadMs: item.uploadLatencyMs,
              inferenceMs: item.inferenceMs,
              downloadMs: item.downloadMs,
            }}
            cacheHit={item.cacheHit}
          />
        )}
        {promptPreview && (
          <Text style={{ fontSize: 12, wordBreak: 'break-word' }}>{promptPreview}</Text>
        )}
        {item.priceMatchKey && (
          <Text type="secondary" style={{ fontSize: 11 }}>
            条目: {item.priceMatchKey}
          </Text>
        )}
        {item.errorCode && (
          <Text type="danger" style={{ fontSize: 11, wordBreak: 'break-word' }}>
            {item.errorCode}: {item.errorMessage?.slice(0, 200)}
          </Text>
        )}
        {hasUpstream && (
          <>
            <Button
              size="small"
              type="link"
              style={{ padding: 0, fontSize: 11 }}
              onClick={() => setExpanded((v) => !v)}
            >
              {expanded ? '隐藏' : '查看'} fal 原始错误
            </Button>
            {expanded && (
              <pre
                style={{
                  fontSize: 11,
                  background: '#fff',
                  padding: 6,
                  borderRadius: 4,
                  maxHeight: 200,
                  overflow: 'auto',
                  margin: 0,
                }}
              >
                {JSON.stringify(item.errorUpstream, null, 2)}
              </pre>
            )}
          </>
        )}
        {thumbnailKey && (
          <CosImage cosKey={thumbnailKey} maxWidth={120} maxHeight={120} />
        )}
        <Button size="small" type="link" style={{ padding: 0 }} onClick={onReuse}>
          复用参数
        </Button>
      </Space>
    </Card>
  );
};

interface DynamicAdminFieldProps {
  field: ImageInputField;
  value: string;
  onChange: (v: string) => void;
}

const DynamicAdminField: React.FC<DynamicAdminFieldProps> = ({ field, value, onChange }) => {
  if (field.type === 'string') {
    return (
      <Form.Item
        label={
          <span>
            {field.label}
            {field.required && <Text type="danger"> *</Text>}
          </span>
        }
      >
        <Input.TextArea
          rows={2}
          value={value}
          placeholder={field.placeholder}
          onChange={(e) => onChange(e.target.value)}
        />
      </Form.Item>
    );
  }
  if (field.type === 'number') {
    return (
      <Form.Item
        label={
          <span>
            {field.label}
            {field.required && <Text type="danger"> *</Text>}
          </span>
        }
      >
        <InputNumber
          value={value === '' ? undefined : Number(value)}
          min={field.min}
          max={field.max}
          step={field.step ?? 1}
          onChange={(v) => onChange(v == null ? '' : String(v))}
          style={{ width: '100%' }}
        />
      </Form.Item>
    );
  }
  if (field.type === 'enum' && field.enumValues) {
    return (
      <Form.Item
        label={
          <span>
            {field.label}
            {field.required && <Text type="danger"> *</Text>}
          </span>
        }
      >
        <Select
          value={value || undefined}
          onChange={(v) => onChange(v)}
          options={field.enumValues.map((v) => ({ label: v, value: v }))}
          placeholder={field.placeholder ?? '请选择'}
          allowClear
        />
      </Form.Item>
    );
  }
  return null;
};

// -------- Drawer -----------

interface DrawerProps {
  open: boolean;
  model: ImageModelDTO | null;
  onClose: () => void;
  onSaved: () => void;
}

const FIELD_TYPES = [
  { value: 'string', label: 'string' },
  { value: 'number', label: 'number' },
  { value: 'enum', label: 'enum' },
  { value: 'image_url', label: 'image_url (单图,字符串)' },
  { value: 'image_urls', label: 'image_urls (单图,数组包装)' },
];

const ModelDrawer: React.FC<DrawerProps> = ({ open, model, onClose, onSaved }) => {
  const [form] = Form.useForm<FormValues>();
  const [saving, setSaving] = useState(false);

  const initial: FormValues = useMemo(() => {
    if (!model) {
      return {
        slug: '',
        endpoint: '',
        displayName: '',
        description: '',
        active: true,
        sortOrder: 0,
        fields: [
          { key: 'prompt', label: '编辑指令', type: 'string', required: true },
          { key: 'image_url', label: '源图', type: 'image_url', required: true },
        ],
        fixedJson: '{}',
        outputImagesPath: 'images',
        outputUrlPath: 'url',
        outputWidthPath: 'width',
        outputHeightPath: 'height',
        outputMimeTypePath: 'content_type',
        pricingEntries: [{ key: 'any', matchJson: '{}', unitPriceUsd: 0.039 }],
        pricingFallbackUsd: null,
        sampleInputJson: '{\n  "prompt": "sample"\n}',
      };
    }
    return {
      slug: model.slug,
      endpoint: model.endpoint,
      displayName: model.displayName,
      description: model.description ?? '',
      active: model.active,
      sortOrder: model.sortOrder,
      fields: (model.inputSchema.fields ?? []).map((f) => ({
        key: f.key,
        label: f.label,
        type: f.type,
        required: f.required,
        defaultValue: f.defaultValue != null ? String(f.defaultValue) : '',
        enumValuesCsv: f.enumValues?.join(',') ?? '',
        min: f.min ?? undefined,
        max: f.max ?? undefined,
        placeholder: f.placeholder ?? '',
      })),
      fixedJson: JSON.stringify(model.inputSchema.fixed ?? {}, null, 2),
      outputImagesPath: model.outputSchema.imagesPath,
      outputUrlPath: model.outputSchema.urlPath,
      outputWidthPath: model.outputSchema.widthPath ?? '',
      outputHeightPath: model.outputSchema.heightPath ?? '',
      outputMimeTypePath: model.outputSchema.mimeTypePath ?? '',
      pricingEntries: (model.pricing.entries ?? []).map((e) => ({
        key: e.key,
        matchJson: JSON.stringify(e.match ?? {}),
        unitPriceUsd: e.unitPriceUsd,
      })),
      pricingFallbackUsd: model.pricing.fallbackUsd ?? null,
      sampleInputJson: JSON.stringify(model.metadata?.sampleInput ?? {}, null, 2),
    };
  }, [model]);

  useEffect(() => {
    if (open) form.setFieldsValue(initial);
  }, [open, initial, form]);

  const handleSubmit = async (values: FormValues) => {
    let fixed: Record<string, unknown>;
    try {
      fixed = values.fixedJson ? JSON.parse(values.fixedJson) : {};
    } catch {
      message.error('inputSchema.fixed 不是合法 JSON');
      return;
    }

    const pricingEntries: ImagePricingEntry[] = [];
    for (const e of values.pricingEntries ?? []) {
      let match: Record<string, string | number>;
      try {
        match = e.matchJson ? JSON.parse(e.matchJson) : {};
      } catch {
        message.error(`pricing 条目 "${e.key}" 的 match 不是合法 JSON`);
        return;
      }
      pricingEntries.push({
        key: e.key,
        match,
        unitPriceUsd: Number(e.unitPriceUsd),
      });
    }

    let sampleInput: Record<string, unknown> = {};
    try {
      sampleInput = values.sampleInputJson ? JSON.parse(values.sampleInputJson) : {};
    } catch {
      message.error('metadata.sampleInput 不是合法 JSON');
      return;
    }

    const fields: ImageInputField[] = (values.fields ?? []).map((f) => {
      const field: ImageInputField = {
        key: f.key,
        label: f.label,
        type: f.type,
        required: !!f.required,
      };
      if (f.defaultValue != null && f.defaultValue !== '') {
        field.defaultValue = f.type === 'number' ? Number(f.defaultValue) : f.defaultValue;
      }
      if (f.type === 'enum' && f.enumValuesCsv) {
        field.enumValues = f.enumValuesCsv
          .split(',')
          .map((s: string) => s.trim())
          .filter(Boolean);
      }
      if (f.type === 'number') {
        if (f.min != null) field.min = Number(f.min);
        if (f.max != null) field.max = Number(f.max);
      }
      if (f.placeholder) field.placeholder = f.placeholder;
      return field;
    });

    const payload: CreateImageModelInput = {
      slug: values.slug,
      endpoint: values.endpoint,
      displayName: values.displayName,
      description: values.description || null,
      inputSchema: { fields, fixed },
      outputSchema: {
        imagesPath: values.outputImagesPath,
        urlPath: values.outputUrlPath,
        widthPath: values.outputWidthPath || undefined,
        heightPath: values.outputHeightPath || undefined,
        mimeTypePath: values.outputMimeTypePath || undefined,
      },
      pricing: {
        currency: 'USD',
        entries: pricingEntries,
        fallbackUsd: values.pricingFallbackUsd ?? undefined,
      },
      active: values.active,
      sortOrder: Number(values.sortOrder ?? 0),
      metadata: { sampleInput },
    };

    setSaving(true);
    try {
      if (model) {
        await request.patch(`/admin/image-models/${model.id}`, payload as UpdateImageModelInput);
        message.success('已保存');
      } else {
        await request.post('/admin/image-models', payload);
        message.success('已创建');
      }
      onSaved();
    } catch (err) {
      message.error((err as Error).message || '保存失败');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      title={model ? `编辑: ${model.slug}` : '新建模型'}
      open={open}
      onClose={onClose}
      width={720}
      extra={
        <Space>
          <Button onClick={onClose}>取消</Button>
          <Button type="primary" loading={saving} onClick={() => form.submit()}>
            保存
          </Button>
        </Space>
      }
    >
      <Form<FormValues> form={form} layout="vertical" onFinish={handleSubmit}>
        <Title level={5}>基本信息</Title>
        <Form.Item name="slug" label="slug（唯一）" rules={[{ required: true }, { pattern: /^[a-z0-9][a-z0-9-]*$/, message: '小写字母、数字、连字符' }]}>
          <Input placeholder="e.g. nano-banana-edit" />
        </Form.Item>
        <Form.Item name="endpoint" label="fal endpoint" rules={[{ required: true }]}>
          <Input placeholder="e.g. fal-ai/nano-banana/edit" />
        </Form.Item>
        <Form.Item name="displayName" label="显示名" rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item name="description" label="描述">
          <Input.TextArea rows={2} />
        </Form.Item>
        <Space>
          <Form.Item name="active" label="启用" valuePropName="checked" initialValue={true}>
            <Switch />
          </Form.Item>
          <Form.Item name="sortOrder" label="排序">
            <InputNumber min={0} />
          </Form.Item>
        </Space>

        <Title level={5}>输入字段</Title>
        <Paragraph type="secondary" style={{ fontSize: 12 }}>
          驱动小程序动态表单和最终发给 fal 的 input object。image_url 字段会在后端自动用源图的 fal URL 替换。
        </Paragraph>
        <Form.List name="fields">
          {(rows, { add, remove }) => (
            <>
              {rows.map((row) => (
                <div
                  key={row.key}
                  style={{ border: '1px dashed #d9d9d9', borderRadius: 4, padding: 8, marginBottom: 8 }}
                >
                  <Space wrap>
                    <Form.Item name={[row.name, 'key']} label="key" rules={[{ required: true }]}>
                      <Input style={{ width: 140 }} />
                    </Form.Item>
                    <Form.Item name={[row.name, 'label']} label="label" rules={[{ required: true }]}>
                      <Input style={{ width: 140 }} />
                    </Form.Item>
                    <Form.Item name={[row.name, 'type']} label="type" rules={[{ required: true }]}>
                      <Select options={FIELD_TYPES} style={{ width: 120 }} />
                    </Form.Item>
                    <Form.Item name={[row.name, 'required']} label="required" valuePropName="checked">
                      <Switch size="small" />
                    </Form.Item>
                    <Form.Item name={[row.name, 'defaultValue']} label="default">
                      <Input style={{ width: 140 }} />
                    </Form.Item>
                    <Form.Item
                      name={[row.name, 'enumValuesCsv']}
                      label="enumValues (逗号分隔)"
                    >
                      <Input style={{ width: 220 }} placeholder="low,medium,high" />
                    </Form.Item>
                    <Form.Item name={[row.name, 'min']} label="min">
                      <InputNumber />
                    </Form.Item>
                    <Form.Item name={[row.name, 'max']} label="max">
                      <InputNumber />
                    </Form.Item>
                    <Button danger onClick={() => remove(row.name)} size="small">
                      删除
                    </Button>
                  </Space>
                </div>
              ))}
              <Button type="dashed" block onClick={() => add({ key: '', label: '', type: 'string', required: false })}>
                + 添加字段
              </Button>
            </>
          )}
        </Form.List>
        <Form.Item
          name="fixedJson"
          label="inputSchema.fixed (JSON, 每次调用都附加的固定参数)"
          style={{ marginTop: 12 }}
        >
          <Input.TextArea rows={3} style={{ fontFamily: 'monospace', fontSize: 12 }} />
        </Form.Item>

        <Title level={5}>输出结构</Title>
        <Space wrap>
          <Form.Item name="outputImagesPath" label="imagesPath" rules={[{ required: true }]}>
            <Input style={{ width: 160 }} />
          </Form.Item>
          <Form.Item name="outputUrlPath" label="urlPath" rules={[{ required: true }]}>
            <Input style={{ width: 160 }} />
          </Form.Item>
          <Form.Item name="outputWidthPath" label="widthPath">
            <Input style={{ width: 160 }} />
          </Form.Item>
          <Form.Item name="outputHeightPath" label="heightPath">
            <Input style={{ width: 160 }} />
          </Form.Item>
          <Form.Item name="outputMimeTypePath" label="mimeTypePath">
            <Input style={{ width: 160 }} />
          </Form.Item>
        </Space>

        <Title level={5}>定价条目</Title>
        <Paragraph type="secondary" style={{ fontSize: 12 }}>
          按顺序匹配。空 match（JSON <code>{'{}'}</code>）= 任何情况都匹配，可用作兜底。
          例如：
          <br />
          固定价：<code>{'{"match": {}, "unitPriceUsd": 0.039}'}</code>
          <br />
          按 quality：<code>{'{"match": {"quality": "high"}, "unitPriceUsd": 0.15}'}</code>
        </Paragraph>
        <Form.List name="pricingEntries">
          {(rows, { add, remove }) => (
            <>
              {rows.map((row) => (
                <div
                  key={row.key}
                  style={{ border: '1px dashed #d9d9d9', borderRadius: 4, padding: 8, marginBottom: 8 }}
                >
                  <Space wrap>
                    <Form.Item name={[row.name, 'key']} label="key" rules={[{ required: true }]}>
                      <Input style={{ width: 160 }} />
                    </Form.Item>
                    <Form.Item
                      name={[row.name, 'matchJson']}
                      label="match JSON"
                      rules={[{ required: true }]}
                    >
                      <Input
                        style={{ width: 300, fontFamily: 'monospace', fontSize: 12 }}
                        placeholder='{"quality":"high"}'
                      />
                    </Form.Item>
                    <Form.Item name={[row.name, 'unitPriceUsd']} label="unitPriceUsd" rules={[{ required: true }]}>
                      <InputNumber min={0} step={0.001} style={{ width: 140 }} />
                    </Form.Item>
                    <Button danger onClick={() => remove(row.name)} size="small">删除</Button>
                  </Space>
                </div>
              ))}
              <Button type="dashed" block onClick={() => add({ key: '', matchJson: '{}', unitPriceUsd: 0 })}>
                + 添加定价条目
              </Button>
            </>
          )}
        </Form.List>
        <Form.Item
          name="pricingFallbackUsd"
          label="fallbackUsd (无条目匹配时用)"
          style={{ marginTop: 12 }}
        >
          <InputNumber min={0} step={0.001} style={{ width: 200 }} />
        </Form.Item>

        <Title level={5}>测试参数 (metadata.sampleInput)</Title>
        <Paragraph type="secondary" style={{ fontSize: 12 }}>
          "测试"按钮会用这个 JSON 作为 input。注意：image_url 字段会被跳过（测试模式不上传源图）。
        </Paragraph>
        <Form.Item name="sampleInputJson">
          <Input.TextArea rows={4} style={{ fontFamily: 'monospace', fontSize: 12 }} />
        </Form.Item>
      </Form>
    </Drawer>
  );
};

interface FormValues {
  slug: string;
  endpoint: string;
  displayName: string;
  description: string;
  active: boolean;
  sortOrder: number;
  fields: Array<{
    key: string;
    label: string;
    type: 'string' | 'number' | 'enum' | 'image_url' | 'image_urls';
    required: boolean;
    defaultValue?: string | number;
    enumValuesCsv?: string;
    min?: number;
    max?: number;
    placeholder?: string;
  }>;
  fixedJson: string;
  outputImagesPath: string;
  outputUrlPath: string;
  outputWidthPath?: string;
  outputHeightPath?: string;
  outputMimeTypePath?: string;
  pricingEntries: Array<{ key: string; matchJson: string; unitPriceUsd: number }>;
  pricingFallbackUsd: number | null;
  sampleInputJson: string;
}

export default ImageModelsPage;
